import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, Spin, message } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import apiService from '../../services/api';
import { useChannels } from '../../hooks/useChannels';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const BAR_COLOUR = COLOR_PRIMARY;
const BAR_HIGHLIGHT = '#ff7a00';

const DemandByDay = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [channel, setChannel] = useState('');
  const { channels } = useChannels({ activeOnly: true });

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (channel) params.channel = channel;
    apiService.reports.getDemandByDay(params)
      .then(res => {
        setData(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => message.error('Failed to load demand data'))
      .finally(() => setLoading(false));
  }, [year, channel]);

  const maxCount = Math.max(...data.map(d => d.count), 1);


  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Demand by Day</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Job volume by day of the week based on collection date
      </Text>

      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <Text strong>Filters:</Text>
          <Select value={year} onChange={setYear} style={{ width: 90 }}>
            {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={channel} onChange={setChannel} style={{ width: 180 }} placeholder="All Channels">
            <Option value="">All Channels</Option>
            {channels.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
          </Select>
          {!loading && (
            <Text type="secondary" style={{ marginLeft: 'auto' }}>
              {total.toLocaleString()} total jobs
            </Text>
          )}
        </div>

        <Spin spinning={loading}>
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tickFormatter={d => d.slice(0, 3)} />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value) => [`${value} jobs (${total ? ((value / total) * 100).toFixed(1) : 0}% of year)`, 'Volume']}
                labelFormatter={(label) => label}
                contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff', borderRadius: 6 }}
                itemStyle={{ color: '#fff' }}
                labelStyle={{ color: '#fff', fontWeight: 600 }}
                cursor={false}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.map((entry) => (
                  <Cell
                    key={entry.day}
                    fill={entry.count === maxCount ? BAR_HIGHLIGHT : BAR_COLOUR}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Spin>
      </Card>
    </div>
  );
};

export default DemandByDay;
